"use client";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import axios from "axios";

const API = "https://erp-production-72da01c8e651.herokuapp.com/api";

const cls = (...c) => c.filter(Boolean).join(" ");

const intentCache = new Map();

const AI_TRIGGER_RE = [
  /(?<![a-zA-Z])\b(158|162|212|250|312|358|362|400|412|600|800|1000|1200)\b(?!\s*['"])/,
  /\b(\d+\/\d+|\d{3,})\s*(flange|leg|\bfl\b)/i,
  /\b\d+\s*mil\b/i,
];

function queryNeedsAI(query) {
  return AI_TRIGGER_RE.some((re) => re.test(query));
}

async function extractSearchIntent(rawQuery, token) {
  const key = rawQuery.trim().toLowerCase();
  if (intentCache.has(key)) return intentCache.get(key);
  if (key.length < 3) return null;
  if (!queryNeedsAI(rawQuery)) return null;

  try {
    const res = await fetch(`${API}/aiSearch/parse-search-intent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ query: rawQuery.slice(0, 150) }),
    });

    if (!res.ok) return null;
    const data = await res.json();
    if (!data.success) return null;

    intentCache.set(key, data.intent);
    return data.intent;
  } catch {
    return null;
  }
}

const ALIASES = {
  "1/2": ["half", '1/2"', "half-inch"],
  "5/8": ['5/8"', "five-eighth"],
  "3/4": ['3/4"'],
  "1-5/8": ['1-5/8"', "1 5/8"],
  "3-5/8": ['3-5/8"', "3 5/8"],
  "2-1/2": ['2-1/2"', "2 1/2"],
  "25ga": ["25 ga", "25gauge", "25g"],
  "20ga": ["20 ga", "20gauge"],
  "18ga": ["18 ga", "18gauge"],
  "16ga": ["16 ga", "16gauge"],
  "14ga": ["14 ga", "14gauge"],
  dw: ["drywall", "dry wall"],
  gb: ["gypsum board", "gyp board"],
  mtl: ["metal"],
  std: ["stud", "studs"],
  cgc: ["cgc"],
  ussg: ["usg", "uss"],
};

// ── Dimension parser ─────────────────────────────────────────────────────
// Handles: 15' 2-3/4"   10'6"   10' 6.5"   10'
//          122"   35-3/4"   119-3/4"   109.5"   109"
//          10.5 (bare decimal)   122 / 35-3/4 (bare, no quote)
function tokenToFeet(s) {
  s = (s || "").trim().replace(/[\u201C\u201D\u02DD\u02BA\u2033\u22EE]/g, '"');
  if (!s) return null;

  let m = s.match(
    /^(\d+(?:\.\d+)?)'[\s]*(\d+(?:\.\d+)?(?:-\d+\/\d+)?(?:\/\d+)?|\d*-\d+\/\d+)"$/,
  );
  if (m) return parseFloat(m[1]) + inchStrToFeet(m[2]);

  m = s.match(/^(\d+(?:\.\d+)?)'$/);
  if (m) return parseFloat(m[1]);

  m = s.match(/^(\d+(?:\.\d+)?(?:-\d+\/\d+)?(?:\/\d+)?)"$/);
  if (m) return inchStrToFeet(m[1]);

  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]);

  m = s.match(/^(\d+(?:-\d+\/\d+)?)$/);
  if (m) return inchStrToFeet(m[1]);

  return null;
}

function inchStrToFeet(s) {
  s = (s || "").trim();
  let m = s.match(/^(\d+)-(\d+)\/(\d+)$/);
  if (m) return (parseInt(m[1]) + parseInt(m[2]) / parseInt(m[3])) / 12;
  m = s.match(/^(\d+)\/(\d+)$/);
  if (m) return parseInt(m[1]) / parseInt(m[2]) / 12;
  m = s.match(/^(\d+(?:\.\d+)?)$/);
  if (m) return parseFloat(m[1]) / 12;
  return 0;
}

function parseSizeCoverage(size, uom) {
  if (!size || !uom) return null;
  const u = uom.trim().toUpperCase();
  const s = size.trim();
  if (u.includes("SF")) {
    const parts = s.split(/\s*[xX×]\s*/);
    if (parts.length === 2) {
      const a = tokenToFeet(parts[0].trim());
      const b = tokenToFeet(parts[1].trim());
      if (a !== null && b !== null) return Math.round(a * b * 10000) / 10000;
    }
    return null;
  }
  if (u.includes("LF")) {
    const ft = tokenToFeet(s);
    return ft !== null ? Math.round(ft * 10000) / 10000 : null;
  }
  return null;
}

const FRACTION_RE = /\d+[-–]\d+\/\d+|\d+\/\d+/g;

function normaliseFractions(str) {
  return (str || "").replace(FRACTION_RE, (m) => m.replace(/[\s\-\/]/g, "_"));
}

function normalise(str) {
  return normaliseFractions(str || "")
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenise(str) {
  return normalise(str)
    .split(/[\s\-\/,]+/)
    .filter(Boolean);
}

const PRODUCT_COMPOUNDS = [
  ["z", "bar"],
  ["z", "furring"],
  ["hat", "channel"],
  ["resilient", "channel"],
  ["rc", "channel"],
  ["corner", "bead"],
  ["l", "bead"],
  ["j", "bead"],
  ["j", "trim"],
  ["cee", "stud"],
  ["drywall", "screw"],
  ["type", "s"],
  ["type", "x"],
  ["tek", "screw"],
  ["lay", "in"],
  ["drop", "ceiling"],
];

function extractCompounds(str) {
  const toks = tokenise(str);
  const found = new Set();
  for (const [a, b] of PRODUCT_COMPOUNDS) {
    for (let i = 0; i < toks.length - 1; i++) {
      if (toks[i] === a && toks[i + 1] === b) {
        found.add(`${a} ${b}`);
      }
    }
    if (toks.includes(`${a}${b}`)) found.add(`${a} ${b}`);
  }
  return found;
}

const DIM_DESCRIPTORS =
  "leg|stud|track|flange|web|wing|return|lip|face|bar|channel|bead";

const DIM_PHRASE_PATTERNS = [
  new RegExp(
    `(\\d+(?:[-–]\\d+\\/\\d+)?(?:\\/\\d+)?)["']?\\s*(${DIM_DESCRIPTORS})`,
    "gi",
  ),
  /(\d+)\s*(ga|gauge|mil)\b/gi,
  /(\d+\/\d+)["']/gi,
];

function extractDimPhrases(str) {
  const norm = normalise(str);
  const phrases = new Set();
  for (const re of DIM_PHRASE_PATTERNS) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(norm)) !== null) {
      const num = normaliseFractions(m[1])
        .replace(/[\s\-\/]/g, "_")
        .toLowerCase();
      const desc = (m[2] || "").toLowerCase();
      const d = desc === "gauge" ? "ga" : desc === "mil" ? "mil" : desc;
      phrases.add(d ? `${num} ${d}` : num);
    }
  }
  return phrases;
}

function scoreItem(item, queryTokens, rawQuery = "", intent = null) {
  if (!queryTokens.length) return { score: 0, matched: [] };

  const uniqueQueryTokens = [...new Set(queryTokens.map(normalise))].filter(
    Boolean,
  );

  const fields = {
    itemCode: normalise(item.itemCode),
    description: normalise(
      item.description ||
        item.costCodeDescription ||
        item.itemDescription ||
        "",
    ),
    size: normalise(item.size),
    section: normalise(item.sectionNumber),
    rest: normalise(
      [item.bidArea, item.uom, item.costCodeDescription]
        .filter(Boolean)
        .join(" "),
    ),
  };

  const descTokens = tokenise(fields.description);
  const allHaystackTokens = tokenise(Object.values(fields).join(" "));
  const allHaystack = Object.values(fields).join(" ");

  let score = 0;
  const matched = [];
  const unmatched = [];

  if (intent) {
    const intentIsReliable =
      intent.productType ||
      intent.gauge ||
      (intent.webSize && /[-\/]/.test(intent.webSize));

    if (intentIsReliable) {
      if (intent.webSize) {
        const normSize = normalise(intent.webSize);
        const sizeTokens = tokenise(intent.webSize);
        const hit =
          allHaystack.includes(normSize) ||
          sizeTokens.every((t) => allHaystackTokens.includes(t));

        if (hit) {
          score += 120;
          matched.push(`size:${intent.webSize}`);
        } else {
          const hasSomeSize = /\d[_\-\/]\d/.test(allHaystack);
          if (hasSomeSize) score -= 80;
        }
      }

      if (intent.productType) {
        const TYPE_KEYWORDS = {
          stud: ["stud", "studs"],
          track: ["track", "tracks"],
          "slotted track": ["slotted", "sdt"],
          "z bar": ["z-bar", "z bar", "zb"],
          "furring channel": ["furring", "fch"],
          "resilient channel": ["resilient", "rch"],
          "flat stock": ["flat stock", "fs"],
          angle: ["angle"],
          "flex track": ["flex", "flexible"],
          "j runner": ["j-runner", "j runner", "jr"],
          "ch stud": ["ch stud", "shaftwall"],
          "carrying channel": ["carrying channel", "cch"],
          "hanger wire": ["hanger wire", "hw"],
          screw: ["screw"],
          insulation: ["insulation", "rockwool", "quietzone"],
          drywall: ["drywall", "gypsum"],
        };

        const keywords = TYPE_KEYWORDS[intent.productType] || [
          intent.productType,
        ];
        const hit = keywords.some((kw) => allHaystack.includes(normalise(kw)));

        if (hit) {
          score += 100;
          matched.push(`type:${intent.productType}`);
        } else {
          score -= 70;
        }
      }

      if (intent.flange) {
        const normFlange = normalise(intent.flange);
        const flangeTokens = tokenise(intent.flange);
        const hit =
          allHaystack.includes(normFlange) ||
          flangeTokens.every((t) => allHaystackTokens.includes(t));

        if (hit) {
          score += 100;
          matched.push(`flange:${intent.flange}`);
        } else {
          const hasFlangeRef =
            allHaystack.includes("flange") || allHaystack.includes("leg");
          if (hasFlangeRef) score -= 60;
        }
      }

      if (intent.gauge) {
        const GAUGE_SIGNALS = {
          25: ["-18", "25ga", "25 ga"],
          20: ["-33", "20ga", "20 ga"],
          18: ["-43", "18ga", "18 ga"],
          16: ["-54", "16ga", "16 ga"],
          14: ["-68", "14ga", "14 ga"],
          12: ["-97", "12ga", "12 ga"],
        };

        const signals = GAUGE_SIGNALS[intent.gauge] || [`${intent.gauge}ga`];
        const hit =
          signals.some((s) => allHaystack.includes(normalise(s))) ||
          signals.some((s) => fields.itemCode.includes(normalise(s)));

        if (hit) {
          score += 100;
          matched.push(`gauge:${intent.gauge}ga`);
        } else {
          const hasGaugeRef =
            /\d{2}ga/.test(allHaystack) || /\-\d{2}$/.test(fields.itemCode);
          if (hasGaugeRef) score -= 60;
        }
      }

      const specifiedFields = [
        intent.webSize ? "size:" : null,
        intent.productType ? "type:" : null,
        intent.flange ? "flange:" : null,
        intent.gauge ? "gauge:" : null,
      ].filter(Boolean);

      const allIntentFieldsMatched =
        specifiedFields.length > 0 &&
        specifiedFields.every((prefix) =>
          matched.some((m) => m.startsWith(prefix)),
        );

      if (allIntentFieldsMatched) score += 50;
    }
  }

  const queryCompounds = extractCompounds(rawQuery);
  const descCompounds = extractCompounds(
    item.description || item.costCodeDescription || item.itemDescription || "",
  );

  let compoundScore = 0;
  const matchedCompoundTokens = new Set();

  for (const qc of queryCompounds) {
    if (descCompounds.has(qc)) {
      compoundScore += 90;
      qc.split(" ").forEach((t) => matchedCompoundTokens.add(t));
    } else {
      compoundScore -= 40;
    }
  }

  const queryDimPhrases = extractDimPhrases(rawQuery);
  const descDimPhrases = extractDimPhrases(
    item.description || item.costCodeDescription || item.itemDescription || "",
  );

  let dimScore = 0;
  const queryDescriptors = new Set();
  const queryDescriptorValues = new Map();

  for (const qp of queryDimPhrases) {
    const parts = qp.includes(" ") ? qp.split(" ") : [qp, ""];
    const val = parts[0];
    const desc = parts[1];
    if (desc) {
      queryDescriptors.add(desc);
      queryDescriptorValues.set(desc, val);
    }
    if (descDimPhrases.has(qp)) dimScore += 80;
  }

  for (const desc of queryDescriptors) {
    const qVal = queryDescriptorValues.get(desc);
    const candidateHasSameDescriptor = [...descDimPhrases].some((p) =>
      p.endsWith(" " + desc),
    );
    const candidateMatchesValue = descDimPhrases.has(`${qVal} ${desc}`);
    if (candidateHasSameDescriptor && !candidateMatchesValue) dimScore -= 60;
  }

  score += dimScore + compoundScore;

  for (const qtNorm of uniqueQueryTokens) {
    if (matchedCompoundTokens.has(qtNorm)) {
      matched.push(qtNorm);
      continue;
    }

    let hitScore = 0;
    const isShortNumeric = /^\d{1,2}$/.test(qtNorm);
    const isFractionToken = /_/.test(qtNorm);

    if (fields.itemCode === qtNorm) hitScore = 100;
    else if (
      !isShortNumeric &&
      qtNorm.length >= 3 &&
      fields.itemCode.startsWith(qtNorm)
    )
      hitScore = 60;
    else if (descTokens.includes(qtNorm)) hitScore = isShortNumeric ? 20 : 50;
    else if (allHaystackTokens.includes(qtNorm))
      hitScore = isShortNumeric ? 12 : 30;
    else {
      let aliasHit = false;
      for (const [canonical, aliases] of Object.entries(ALIASES)) {
        const allForms = [canonical, ...aliases].map(normalise);
        if (allForms.includes(qtNorm)) {
          if (
            allForms.some(
              (f) => allHaystackTokens.includes(f) || allHaystack.includes(f),
            )
          ) {
            hitScore = 20;
            aliasHit = true;
            break;
          }
        }
      }
      if (
        !aliasHit &&
        !isShortNumeric &&
        !isFractionToken &&
        qtNorm.length >= 3 &&
        allHaystack.includes(qtNorm)
      )
        hitScore = 5;
    }

    if (hitScore > 0) {
      score += hitScore;
      matched.push(qtNorm);
    } else {
      unmatched.push(qtNorm);
    }
  }

  const intentMatchedWell = matched.some((m) => m.includes(":"));
  for (const ut of unmatched) {
    const isNumeric = /^\d+$/.test(ut);
    const isFraction = /_/.test(ut) || /\d+[-\/]\d/.test(ut);
    const base = isNumeric ? 10 : isFraction ? 25 : 25;
    score -= intentMatchedWell ? base * 0.4 : base;
  }

  if (matched.length === uniqueQueryTokens.length) score += 30;

  const contentTokens = uniqueQueryTokens.filter((t) => !/^\d+$/.test(t));
  if (
    contentTokens.length > 0 &&
    contentTokens.every(
      (t) => descTokens.includes(t) || matchedCompoundTokens.has(t),
    )
  )
    score += 40;

  return { score, matched };
}

function Highlight({ text = "", tokens = [] }) {
  if (!tokens.length) return <span>{text}</span>;
  const lower = text.toLowerCase();
  const ranges = [];
  for (const t of tokens) {
    const tn = normalise(t);
    let idx = 0;
    while ((idx = lower.indexOf(tn, idx)) !== -1) {
      ranges.push([idx, idx + tn.length]);
      idx += tn.length;
    }
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged = [];
  for (const [s, e] of ranges) {
    if (merged.length && s <= merged[merged.length - 1][1])
      merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], e);
    else merged.push([s, e]);
  }
  const parts = [];
  let cursor = 0;
  for (const [s, e] of merged) {
    if (s > cursor) parts.push(text.slice(cursor, s));
    parts.push(
      <mark
        key={s}
        className="bg-orange-500/30 text-orange-200 rounded-sm px-0.5"
      >
        {text.slice(s, e)}
      </mark>,
    );
    cursor = e;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return <span>{parts}</span>;
}

function Badge({ label, color = "gray" }) {
  const colors = {
    orange: "bg-orange-500/20 text-orange-300 border-orange-500/30",
    blue: "bg-blue-500/20 text-blue-300 border-blue-500/30",
    teal: "bg-teal-500/20 text-teal-300 border-teal-500/30",
    green: "bg-green-500/20 text-green-300 border-green-500/30",
    yellow: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
    gray: "bg-gray-600/50 text-gray-400 border-gray-600",
  };
  return (
    <span
      className={cls(
        "inline-block px-1.5 py-0.5 text-[9px] font-semibold rounded border tracking-wide whitespace-nowrap",
        colors[color] || colors.gray,
      )}
    >
      {label}
    </span>
  );
}

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const isCoverageBased = (uom = "") =>
  ["SF", "LF"].some((u) => uom.trim().toUpperCase().includes(u));

// ── Mini order-progress bar ────────────────────────────────────────────────
// Same visual language as PODetailView3's QtyBar: two-layer bar showing
// ordered (dim) vs delivered (bright) against the net budget qty. Only
// rendered for true budget-origin sizes — unbudgeted/"ordered" extras don't
// have a budget qty to compare against, so we show a simple ordered/delivered
// count instead (no bar, per PM guidance: section total covers that case).
function OrderProgress({ qtyOrdered = 0, qtyDelivered = 0, netQty = 0 }) {
  if (!qtyOrdered && !netQty) return null;
  const target = netQty > 0 ? netQty : qtyOrdered;
  const orderedPct =
    target > 0 ? Math.min(100, (qtyOrdered / target) * 100) : 0;
  const deliveredPct =
    target > 0 ? Math.min(100, (qtyDelivered / target) * 100) : 0;
  const isOver = qtyOrdered > target && target > 0;

  return (
    <div className="flex items-center gap-1.5 mt-1">
      <div className="relative flex-1 bg-gray-700 rounded-full h-1.5 overflow-hidden min-w-[50px]">
        <div
          className={cls(
            "absolute left-0 top-0 h-full rounded-full",
            isOver ? "bg-red-500/50" : "bg-yellow-600/50",
          )}
          style={{ width: `${orderedPct}%` }}
        />
        <div
          className={cls(
            "absolute left-0 top-0 h-full rounded-full transition-all",
            deliveredPct >= 100 ? "bg-green-500" : "bg-orange-500",
          )}
          style={{ width: `${deliveredPct}%` }}
        />
      </div>
      <span
        className={cls(
          "text-[9px] font-mono shrink-0 whitespace-nowrap",
          isOver ? "text-red-400 font-semibold" : "text-gray-500",
        )}
      >
        {fmtQty(qtyDelivered)}/{fmtQty(target)} on budget
        {isOver && ` ⚠+${fmtQty(qtyOrdered - target)}`}
      </span>
    </div>
  );
}

/**
 * ItemSearchHelper3
 *
 * Props:
 *   token               – JWT for API calls
 *   onSelect            – callback(item, overrideQty?) — modal stays open,
 *                          NEVER call onClose() as a side-effect of this.
 *   onClose             – called when user clicks Done / Cancel / Esc
 *   purchaseOrder       – optional PO id string (used for recommended ranking)
 *   recommendedBudgetItems – array of budget items from the selected PO,
 *                            pre-enriched with suggestedQty, unitCost, size,
 *                            origin ("budget" | "ordered"), qtyOrdered,
 *                            qtyDelivered, netQty (budget qty + CO delta, if
 *                            available — falls back to suggestedQty)
 *   title               – optional modal title override
 */
export default function ItemSearchHelper3({
  token,
  onSelect,
  onClose,
  purchaseOrder = null,
  recommendedBudgetItems = [],
  title = "Select Item",
}) {
  const [query, setQuery] = useState("");
  const [rawItems, setRawItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addCounts, setAddCounts] = useState({}); // key(itemCode||size) -> total qty added
  const [activeIdx, setActiveIdx] = useState(0);
  const [selectedSizeByCode, setSelectedSizeByCode] = useState({}); // itemCode -> selected size pill
  const inputRef = useRef(null);
  const rowRefs = useRef({});

  const [intent, setIntent] = useState(null);
  const [intentLoading, setIntentLoading] = useState(false);

  // 'budget' | 'database' | 'all' — which item source to browse/search
  const [viewMode, setViewMode] = useState(
    recommendedBudgetItems.length > 0 ? "budget" : "database",
  );

  // Toast feedback shown on every add — modal never closes on add
  const [toast, setToast] = useState(null);
  const toastTimer = useRef(null);
  const showToast = useCallback((text) => {
    setToast({ text, id: Date.now() });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2000);
  }, []);
  useEffect(
    () => () => toastTimer.current && clearTimeout(toastTimer.current),
    [],
  );

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API}/item-database/catalog?category=M&isActive=true`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setRawItems(res.data.items || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadItems();
    setTimeout(() => inputRef.current?.focus(), 80);
  }, [loadItems]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape" && document.activeElement !== inputRef.current) {
        onClose();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const queryTokens = useMemo(() => tokenise(query), [query]);
  const isSearching = query.trim().length > 0;

  useEffect(() => {
    if (!query.trim() || query.trim().length < 3) {
      setIntent(null);
      return;
    }
    const timer = setTimeout(async () => {
      setIntentLoading(true);
      const result = await extractSearchIntent(query, token);
      setIntent(result);
      setIntentLoading(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [query, token]);

  // ── Group recommendedBudgetItems by itemCode → { sizes: [...] } ──────────
  // This is what powers the size-pill UI: one card per itemCode, every size
  // that's been budgeted or previously ordered shows as a pill on that card.
  const groupedRecommended = useMemo(() => {
    const byCode = new Map();
    recommendedBudgetItems.forEach((rec) => {
      const code = rec.itemCode || "—";
      if (!byCode.has(code)) {
        byCode.set(code, {
          itemCode: code,
          description: rec.description || rec.costCodeDescription,
          uom: rec.uom,
          sectionNumber: rec.sectionNumber,
          coveragePerUnit: rec.coveragePerUnit,
          defaultVendor: rec.defaultVendor,
          itemDatabaseId: rec.itemDatabaseId,
          vendorPrices: rec.vendorPrices || [],
          sizes: [],
        });
      }
      byCode.get(code).sizes.push(rec);
    });
    // Sort sizes within each card: budget-origin first, then by size string
    byCode.forEach((card) => {
      card.sizes.sort((a, b) => {
        if (a.origin !== b.origin) return a.origin === "budget" ? -1 : 1;
        return (a.size || "").localeCompare(b.size || "");
      });
    });
    return [...byCode.values()].sort((a, b) =>
      (a.itemCode || "").localeCompare(b.itemCode || ""),
    );
  }, [recommendedBudgetItems]);

  const poItemCodes = useMemo(() => {
    const codes = new Set();
    recommendedBudgetItems.forEach((i) => {
      if (i.itemCode) codes.add(i.itemCode);
    });
    return codes;
  }, [recommendedBudgetItems]);

  const poOrderedOnlyCodes = useMemo(() => {
    const codes = new Set();
    const byCode = new Map();
    recommendedBudgetItems.forEach((i) => {
      if (!i.itemCode) return;
      if (!byCode.has(i.itemCode)) byCode.set(i.itemCode, []);
      byCode.get(i.itemCode).push(i);
    });
    byCode.forEach((sizes, code) => {
      if (sizes.every((s) => s.origin === "ordered")) codes.add(code);
    });
    return codes;
  }, [recommendedBudgetItems]);

  const poSections = useMemo(() => {
    const s = new Set();
    recommendedBudgetItems.forEach((i) => {
      if (i.sectionNumber) s.add(i.sectionNumber);
    });
    return s;
  }, [recommendedBudgetItems]);

  // ── Search results ─────────────────────────────────────────────────────
  const scoredAll = useMemo(() => {
    if (!queryTokens.length) {
      return rawItems
        .map((item) => ({ item, score: 0, matched: [] }))
        .sort((a, b) =>
          (a.item.itemCode || "").localeCompare(b.item.itemCode || ""),
        );
    }
    return rawItems
      .map((item) => {
        const { score, matched } = scoreItem(item, queryTokens, query, intent);
        return { item, score, matched };
      })
      .filter((r) => r.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [rawItems, queryTokens, query, intent]);

  const scoredBudget = useMemo(
    () => scoredAll.filter((r) => poItemCodes.has(r.item.itemCode)),
    [scoredAll, poItemCodes],
  );
  const scoredDatabase = useMemo(
    () => scoredAll, // Database = full catalog, including budget items
    [scoredAll],
  );

  const scored =
    viewMode === "budget"
      ? scoredBudget
      : viewMode === "database"
        ? scoredDatabase
        : scoredAll;

  useEffect(() => setActiveIdx(0), [scored.length, viewMode, query]);

  // ── Merge recommended data into search results so budget/ordered cards
  //    render identically whether you're browsing or typing a query. This is
  //    the fix for "budget items look different once I start searching."
  const recommendedByCode = useMemo(() => {
    const m = new Map();
    groupedRecommended.forEach((card) => m.set(card.itemCode, card));
    return m;
  }, [groupedRecommended]);

  // Group search results by itemCode too, so cards are consistent everywhere
  const groupedSearchResults = useMemo(() => {
    const byCode = new Map();
    scored.forEach(({ item, matched }) => {
      const code = item.itemCode || item._id;
      if (!byCode.has(code)) {
        const recCard = recommendedByCode.get(item.itemCode);
        byCode.set(code, {
          itemCode: item.itemCode || "—",
          description:
            item.description ||
            item.costCodeDescription ||
            item.itemDescription,
          uom: item.uom,
          sectionNumber: item.sectionNumber || recCard?.sectionNumber,
          coveragePerUnit: item.coveragePerUnit,
          defaultVendor: item.defaultVendor,
          defaultCost: item.defaultCost,
          itemDatabaseId: item._id,
          vendorPrices: item.vendorPrices || [],
          matched,
          // If this code has budget/order history, splice those sizes in as
          // pills too — otherwise fall back to the single catalog size.
          sizes: recCard
            ? recCard.sizes
            : [
                {
                  itemCode: item.itemCode,
                  size: item.size,
                  uom: item.uom,
                  unitCost: resolveCatalogDisplayPrice(item),
                  defaultCost: resolveCatalogDisplayPrice(item),
                  coveragePerUnit: item.coveragePerUnit,
                  origin: null,
                  itemDatabaseId: item._id,
                  suggestedQty: 1,
                },
              ],
          _rawItem: item,
        });
      }
    });
    return [...byCode.values()];
  }, [scored, recommendedByCode]);

  // Resolve a display price for a raw catalog item from its defaultVendor's
  // entry in vendorPrices — catalog items don't carry a flat defaultCost,
  // pricing only exists per-vendor.
  function resolveCatalogDisplayPrice(item) {
    const defaultVendorId =
      item.defaultVendor?._id?.toString() ||
      (typeof item.defaultVendor === "string" ? item.defaultVendor : "") ||
      "";

    if (defaultVendorId && item.vendorPrices?.length) {
      const vp = item.vendorPrices.find(
        (v) => (v.vendor?._id || v.vendor)?.toString() === defaultVendorId,
      );
      if (vp) return vp.price || vp.unitRate || 0;
    }

    // No default vendor price on file — fall back to the cheapest priced
    // vendor if any exist, so the card still shows something useful.
    if (item.vendorPrices?.length) {
      const withPrice = item.vendorPrices
        .map((vp) => vp.price || vp.unitRate || 0)
        .filter((p) => p > 0);
      if (withPrice.length) return Math.min(...withPrice);
    }

    return 0;
  }

  const cardsToShow = isSearching
    ? groupedSearchResults
    : viewMode === "budget"
      ? groupedRecommended
      : (viewMode === "database"
          ? rawItems // full catalog, including budget items
          : rawItems
        ).map((item) => ({
          itemCode: item.itemCode || "—",
          description:
            item.description ||
            item.costCodeDescription ||
            item.itemDescription,
          uom: item.uom,
          sectionNumber: item.sectionNumber,
          coveragePerUnit: item.coveragePerUnit,
          defaultVendor: item.defaultVendor,
          defaultCost: resolveCatalogDisplayPrice(item),
          itemDatabaseId: item._id,
          vendorPrices: item.vendorPrices || [],
          sizes: [
            {
              itemCode: item.itemCode,
              size: item.size,
              uom: item.uom,
              unitCost: resolveCatalogDisplayPrice(item),
              defaultCost: resolveCatalogDisplayPrice(item),
              coveragePerUnit: item.coveragePerUnit,
              origin: null,
              itemDatabaseId: item._id,
              suggestedQty: 1,
            },
          ],
          _rawItem: item,
        }));

  // ── Add — modal NEVER closes here. onSelect must not trigger onClose. ──
  // ── Add — modal NEVER closes here. onSelect must not trigger onClose. ──
  // ── Add — modal NEVER closes here. onSelect must not trigger onClose. ──
  const handleAddSize = useCallback(
    (card, sizeEntry, qty) => {
      const exactMatch = sizeEntry.itemDatabaseId
        ? rawItems.find(
            (r) => r._id?.toString() === sizeEntry.itemDatabaseId?.toString(),
          )
        : null;

      const codeMatch =
        exactMatch || rawItems.find((r) => r.itemCode === card.itemCode);

      // uom and coveragePerUnit are physical facts about the ITEM CODE, not
      // about the specific size/order-history row — they must come from the
      // authoritative catalog record (rawItems) whenever one exists. Trusting
      // sizeEntry/card here is what causes SF/LF items to silently render as
      // flat EA: the recommendation projection (bi.uom || bi.container from
      // the PO-items endpoint) can carry a wrong or missing uom that then
      // shadows the correct catalog value.
      const catalogUom = codeMatch?.uom;
      const catalogCoverage = codeMatch?.coveragePerUnit;

      // size legitimately varies per row (that's the whole point of size
      // pills), so it still comes from the pill the user picked — but if
      // the catalog record's uom disagrees with what the size pill implies,
      // the catalog wins.
      const uom = catalogUom || sizeEntry.uom || card.uom;
      const coveragePerUnit =
        catalogCoverage || sizeEntry.coveragePerUnit || card.coveragePerUnit;

      const baseItem = {
        _id:
          exactMatch?._id ||
          sizeEntry.itemDatabaseId ||
          card.itemDatabaseId ||
          codeMatch?._id,
        itemCode: card.itemCode,
        description:
          codeMatch?.description ||
          codeMatch?.costCodeDescription ||
          codeMatch?.itemDescription ||
          card.description ||
          "",
        size: sizeEntry.size,
        uom,
        sectionNumber: card.sectionNumber,
        // defaultCost is only a fallback rate used when no vendor price is
        // found — leave as-is, but note it's meaningless without a correct
        // uom, which is now guaranteed above.
        defaultCost:
          sizeEntry.unitCost ?? sizeEntry.defaultCost ?? card.defaultCost,
        coveragePerUnit,
        vendorPrices: codeMatch?.vendorPrices || card.vendorPrices || [],
        defaultVendor: codeMatch?.defaultVendor || card.defaultVendor,
      };

      onSelect(baseItem, qty);

      const key = card.itemCode + "||" + (sizeEntry.size || "");
      setAddCounts((prev) => ({ ...prev, [key]: (prev[key] || 0) + qty }));
      showToast(
        `Added ${card.itemCode}${sizeEntry.size ? ` · ${sizeEntry.size}` : ""} · qty ${qty}`,
      );
    },
    [onSelect, rawItems, showToast],
  );

  const handleAddAllBudget = useCallback(() => {
    let count = 0;
    groupedRecommended.forEach((card) => {
      card.sizes.forEach((sizeEntry) => {
        const key = card.itemCode + "||" + (sizeEntry.size || "");
        if (!addCounts[key]) {
          handleAddSize(card, sizeEntry, sizeEntry.suggestedQty || 1);
          count++;
        }
      });
    });
    if (count === 0) {
      showToast("All items already added — adjust qty to add more");
    }
  }, [groupedRecommended, addCounts, handleAddSize, showToast]);

  const scrollTo = (idx) =>
    rowRefs.current[idx]?.scrollIntoView({ block: "nearest" });

  const handleKey = (e) => {
    if (!isSearching) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.min(i + 1, cardsToShow.length - 1);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => {
        const n = Math.max(i - 1, 0);
        scrollTo(n);
        return n;
      });
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  const totalAdded = Object.values(addCounts).reduce((s, n) => s + n, 0);
  const allBudgetAdded =
    groupedRecommended.length > 0 &&
    groupedRecommended.every((card) =>
      card.sizes.every(
        (s) => addCounts[card.itemCode + "||" + (s.size || "")] > 0,
      ),
    );

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onKeyDown={handleKey}
    >
      <div className="relative bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl flex flex-col w-full max-w-6xl mx-auto max-h-[90vh]">
        {/* ── Header ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/70 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-orange-500/20 border border-orange-500/30 flex items-center justify-center shrink-0">
              <svg
                className="w-4 h-4 text-orange-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
                />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-white truncate">
                {title}
              </h3>
              <p className="text-[10px] text-gray-500 mt-0.5">
                {groupedRecommended.length > 0 ? (
                  <>
                    <span className="text-teal-400 font-medium">
                      {groupedRecommended.length} items
                    </span>{" "}
                    from budget &amp; order history · search stays in the same
                    view · add as many as you need
                  </>
                ) : (
                  "Search the item catalog · add as many as you need"
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {totalAdded > 0 && (
              <span className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 bg-green-900/30 border border-green-700/40 rounded-lg text-[11px] font-semibold text-green-300">
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {totalAdded} added
              </span>
            )}
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-white hover:bg-gray-700 transition-colors text-lg leading-none"
              title="Close (items already added are kept)"
            >
              ×
            </button>
          </div>
        </div>

        {/* ── Search bar + source toggle ── */}
        <div className="px-5 py-3 border-b border-gray-700/50 shrink-0 space-y-2.5">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by code, description, size, gauge, section…"
              className="w-full pl-10 pr-9 py-2.5 text-sm bg-gray-800 border border-gray-600 rounded-xl text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none transition-colors"
            />
            {intentLoading && (
              <span className="absolute right-9 top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 border-gray-600 border-t-orange-400 rounded-full animate-spin" />
            )}
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-base leading-none"
              >
                ×
              </button>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-1 bg-gray-800/60 border border-gray-700 rounded-xl p-1">
              {[
                [
                  "budget",
                  "★ Budget",
                  isSearching
                    ? groupedSearchResults.filter((c) =>
                        poItemCodes.has(c.itemCode),
                      ).length
                    : groupedRecommended.length,
                ],
                [
                  "database",
                  "Database",
                  isSearching ? groupedSearchResults.length : rawItems.length,
                ],
                [
                  "all",
                  "All Items",
                  isSearching ? groupedSearchResults.length : rawItems.length,
                ],
              ].map(([id, label, count]) => (
                <button
                  key={id}
                  onClick={() => setViewMode(id)}
                  className={cls(
                    "px-3 py-1.5 text-[11px] font-semibold rounded-lg transition-colors flex items-center gap-1.5",
                    viewMode === id
                      ? id === "budget"
                        ? "bg-teal-600 text-white"
                        : "bg-orange-500 text-white"
                      : "text-gray-400 hover:text-white hover:bg-gray-700/60",
                  )}
                >
                  {label}
                  {count !== null && (
                    <span
                      className={cls(
                        "text-[9px] px-1.5 py-0.5 rounded-full font-mono",
                        viewMode === id
                          ? "bg-black/20"
                          : "bg-gray-700 text-gray-400",
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 flex-wrap min-w-0">
              {queryTokens.length > 0 && (
                <div className="flex items-center gap-1.5 flex-wrap">
                  {queryTokens.map((t) => (
                    <span
                      key={t}
                      className="px-1.5 py-0.5 text-[9px] font-mono bg-gray-700 text-gray-300 rounded border border-gray-600"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              <span className="text-[10px] text-gray-600 shrink-0 whitespace-nowrap">
                {loading
                  ? "Loading catalog…"
                  : isSearching
                    ? `${cardsToShow.length} match${cardsToShow.length !== 1 ? "es" : ""}`
                    : viewMode === "budget"
                      ? `${groupedRecommended.length} items on budget`
                      : `${rawItems.length} in catalog`}
                {isSearching && " · ↑↓ · Esc close"}
              </span>
              {viewMode === "budget" &&
                groupedRecommended.length > 0 &&
                !isSearching && (
                  <button
                    onClick={handleAddAllBudget}
                    disabled={allBudgetAdded}
                    className={cls(
                      "px-3 py-1 text-[10px] font-semibold rounded-lg transition-colors flex items-center gap-1.5 ml-2",
                      allBudgetAdded
                        ? "bg-green-900/30 text-green-400 border border-green-700/40 cursor-default"
                        : "bg-teal-700 hover:bg-teal-600 text-white",
                    )}
                  >
                    {allBudgetAdded ? "All added ✓" : "Add All"}
                  </button>
                )}
            </div>
          </div>
        </div>

        {/* ── Body — unified card list, same layout in every mode ── */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {error && (
            <div className="px-4 py-3 bg-red-900/40 border border-red-700 rounded-lg text-sm text-red-300">
              {error}
            </div>
          )}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
              <span className="text-gray-500 text-sm">
                Loading item catalog…
              </span>
            </div>
          )}

          {!loading && cardsToShow.length === 0 && !error && (
            <div className="flex flex-col items-center justify-center py-20 gap-2 text-gray-500 px-6 text-center">
              <svg
                className="w-10 h-10 opacity-30"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-sm">
                {isSearching
                  ? `No items match “${query}” in this view.`
                  : viewMode === "budget"
                    ? "No budget items or order history for this PO yet."
                    : "No items found."}
              </p>
              {viewMode !== "all" && (
                <button
                  onClick={() => setViewMode("all")}
                  className="text-xs text-orange-400 hover:text-orange-300 underline underline-offset-2"
                >
                  Search all items instead
                </button>
              )}
            </div>
          )}

          {!loading &&
            cardsToShow.map((card, idx) => (
              <ItemCard
                key={card.itemCode + idx}
                card={card}
                isActive={isSearching && idx === activeIdx}
                isOrderedOnly={poOrderedOnlyCodes.has(card.itemCode)}
                isBudget={poItemCodes.has(card.itemCode)}
                isPoSection={poSections.has(card.sectionNumber)}
                queryTokens={queryTokens}
                addCounts={addCounts}
                selectedSize={selectedSizeByCode[card.itemCode]}
                onSelectSize={(size) =>
                  setSelectedSizeByCode((p) => ({
                    ...p,
                    [card.itemCode]: size,
                  }))
                }
                onAdd={(sizeEntry, qty) => handleAddSize(card, sizeEntry, qty)}
                onMouseEnter={() => isSearching && setActiveIdx(idx)}
                rowRef={(el) => {
                  if (rowRefs?.current) rowRefs.current[idx] = el;
                }}
              />
            ))}
        </div>

        {/* ── Toast ── */}
        {toast && (
          <div
            key={toast.id}
            className="absolute bottom-16 left-1/2 -translate-x-1/2 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-full shadow-lg shadow-black/30 flex items-center gap-2 pointer-events-none animate-[fadeIn_0.15s_ease-out]"
          >
            <svg
              className="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
            {toast.text}
          </div>
        )}

        {/* ── Footer ── */}
        <div className="px-5 py-3 border-t border-gray-700/70 shrink-0 flex items-center justify-between gap-3">
          <div className="flex items-center gap-4 text-[10px] text-gray-600">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-teal-500/60 inline-block" />
              Adding an item keeps this window open — add as many as you need
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {totalAdded > 0 && (
              <span className="sm:hidden text-xs text-teal-400 font-medium">
                {totalAdded} added
              </span>
            )}
            <button
              onClick={onClose}
              className="px-5 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
            >
              {totalAdded > 0 ? `Done (${totalAdded} added)` : "Cancel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Unified item card — same component whether browsing budget, browsing
//    the catalog, or viewing search results. Multiple sizes for the same
//    itemCode render as pills; selecting a pill reveals its qty+Add control
//    and (for budget-origin sizes) an order-progress bar sourced the same
//    way PODetailView3 computes it: ordered/delivered against net budget qty.
// ─────────────────────────────────────────────────────────────────────────
function ItemCard({
  card,
  isActive,
  isOrderedOnly,
  isBudget,
  isPoSection,
  queryTokens,
  addCounts,
  selectedSize,
  onSelectSize,
  onAdd,
  onMouseEnter,
  rowRef,
}) {
  const sizes = card.sizes || [];
  const hasMultipleSizes = sizes.length > 1;
  const activeSize =
    sizes.find((s) => s.size === selectedSize) || sizes[0] || {};
  const covered = isCoverageBased(activeSize.uom || card.uom);
  const [qty, setQty] = useState(activeSize.suggestedQty || 1);

  useEffect(() => {
    setQty(activeSize.suggestedQty || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSize.size]);

  const activeKey = card.itemCode + "||" + (activeSize.size || "");
  const timesAddedQty = addCounts[activeKey] || 0;
  const price =
    activeSize.unitCost || activeSize.defaultCost || card.defaultCost || 0;

  return (
    <div
      ref={rowRef}
      onMouseEnter={onMouseEnter}
      className={cls(
        "rounded-xl border p-3 transition-colors",
        isActive
          ? "border-orange-500 bg-orange-500/5"
          : isOrderedOnly
            ? "border-blue-800/40 bg-blue-900/5 hover:border-blue-700/60"
            : isBudget
              ? "border-teal-800/40 bg-teal-900/5 hover:border-teal-700/60"
              : "border-gray-700 bg-gray-800/40 hover:border-gray-600",
      )}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="font-mono text-orange-400 font-semibold text-[12px]">
              <Highlight text={card.itemCode || "—"} tokens={queryTokens} />
            </span>
            {isOrderedOnly && (
              <Badge label="↩ PREVIOUSLY ORDERED" color="blue" />
            )}
            {isBudget && !isOrderedOnly && (
              <Badge label="★ BUDGET" color="teal" />
            )}
            {!isBudget && isPoSection && (
              <Badge label="PO SECTION" color="blue" />
            )}
            {card.sectionNumber && (
              <span className="text-[10px] font-mono text-gray-500">
                §{card.sectionNumber}
              </span>
            )}
          </div>
          <p className="text-[13px] text-gray-100 font-medium leading-snug">
            <Highlight text={card.description || "—"} tokens={queryTokens} />
          </p>
          {card.defaultVendor?.companyName && (
            <span className="text-[10px] text-gray-500">
              {card.defaultVendor.companyName}
            </span>
          )}
        </div>
      </div>

      {/* Size pills */}
      <div className="flex items-center gap-1.5 flex-wrap mt-2.5">
        {sizes.map((s) => {
          const key = card.itemCode + "||" + (s.size || "");
          const isSel = s.size === activeSize.size;
          const addedQty = addCounts[key] || 0;
          const sizeOrderedOnly = s.origin === "ordered";
          return (
            <button
              key={key}
              type="button"
              onClick={() => onSelectSize(s.size)}
              className={cls(
                "px-2.5 py-1 text-[11px] font-mono rounded-lg border transition-colors flex items-center gap-1.5",
                isSel
                  ? sizeOrderedOnly
                    ? "bg-blue-600 border-blue-500 text-white"
                    : "bg-teal-600 border-teal-500 text-white"
                  : addedQty > 0
                    ? "bg-green-900/30 border-green-700/50 text-green-300 hover:border-green-600"
                    : "bg-gray-700/60 border-gray-600 text-gray-300 hover:border-gray-500 hover:text-white",
              )}
              title={
                hasMultipleSizes
                  ? sizeOrderedOnly
                    ? "Previously ordered at this size"
                    : "On budget at this size"
                  : undefined
              }
            >
              {s.size || "std"}
              {addedQty > 0 && (
                <span className="text-[9px] opacity-90">✓{addedQty}</span>
              )}
            </button>
          );
        })}
        {!sizes.length && (
          <span className="text-[10px] text-gray-600 italic">No size</span>
        )}
      </div>

      {/* Order progress for the selected size — budget-origin only.
          Unbudgeted/"ordered" extras don't have a budget qty to compare
          against; the PO's section total already covers that case. */}
      {activeSize.origin === "budget" &&
        (activeSize.qtyOrdered > 0 || activeSize.netQty > 0) && (
          <OrderProgress
            qtyOrdered={activeSize.qtyOrdered || 0}
            qtyDelivered={activeSize.qtyDelivered || 0}
            netQty={activeSize.netQty || activeSize.suggestedQty || 0}
          />
        )}
      {activeSize.origin === "ordered" && activeSize.qtyOrdered > 0 && (
        <p className="text-[9px] text-blue-400 font-mono mt-1">
          {fmtQty(activeSize.qtyDelivered || 0)}/{fmtQty(activeSize.qtyOrdered)}{" "}
          delivered so far (unbudgeted — see PO section total)
        </p>
      )}

      {/* Details + qty + add */}
      <div className="flex items-end justify-between gap-3 mt-2.5 pt-2.5 border-t border-gray-700/50">
        <div className="flex items-center gap-3 text-[11px] text-gray-400">
          <span
            className={cls(
              "font-semibold px-1.5 py-0.5 rounded",
              covered
                ? "text-blue-300 bg-blue-900/30"
                : "text-gray-400 bg-gray-700/50",
            )}
          >
            {covered ? "PC" : activeSize.uom || card.uom || "EA"}
          </span>
          {covered && (activeSize.coveragePerUnit || card.coveragePerUnit) && (
            <span className="font-mono text-blue-400">
              {activeSize.coveragePerUnit || card.coveragePerUnit}{" "}
              {activeSize.uom || card.uom}/PC
            </span>
          )}
          {price > 0 && (
            <span className="font-mono text-gray-300">
              ${fmt(price)}/{activeSize.uom || card.uom || "EA"}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-1">
            <span className="text-[9px] text-gray-500">Qty</span>
            <input
              type="number"
              value={qty}
              min="0"
              step="1"
              onChange={(e) => setQty(e.target.value)}
              className="w-16 px-2 py-1 text-xs bg-gray-700 border border-gray-600 rounded text-gray-200 text-right focus:border-orange-500 focus:outline-none"
            />
          </div>
          <button
            onClick={() => onAdd(activeSize, parseFloat(qty) || 1)}
            className={cls(
              "px-3 py-1 text-[11px] font-semibold rounded-lg transition-colors whitespace-nowrap flex items-center gap-1",
              timesAddedQty > 0
                ? "bg-green-700 hover:bg-green-600 text-white"
                : isOrderedOnly
                  ? "bg-blue-600 hover:bg-blue-500 text-white"
                  : isBudget
                    ? "bg-teal-600 hover:bg-teal-500 text-white"
                    : "bg-orange-500 hover:bg-orange-600 text-white",
            )}
          >
            {timesAddedQty > 0
              ? `Add More (${timesAddedQty} in cart)`
              : "Add →"}
          </button>
        </div>
      </div>
    </div>
  );
}
